/**
 * «Cómo hacer buenas fotos» — which video each language plays, and what the player reports.
 *
 * Why this exists: the photo decides the AI's verdict, and María recorded a 1:44 video that teaches what a
 * good one is. PhotoVideoTutorialScreen (docs/tutorial-video-buenas-fotos.md) plays it from Ajustes → Cámara.
 * One video per app language, each spoken and lettered in it: a language without its video shows no row,
 * rather than a Spanish video in a French kitchen.
 *
 * The files live in the public bucket `bronze-tracker-resources` (eu-central-1), not in `public/`: Amplify's
 * single-page rewrite sends any `.mp4` to index.html, and 30 MB per language do not belong in git. S3
 * answers byte ranges, which is what `<video>` streams with. Names carry a version (`-v1`): a re-cut
 * video is uploaded as `-v2` and the name changes here, so no tablet keeps the old one from its cache.
 *
 * Telemetry rides our own forensic lane (src/forensicEvents.js → Athena `bronze_tracker_forensic.events`),
 * not PostHog; the server stamps device and restaurant. Best-effort: it never throws into the player.
 */
import { reportForensicEvent } from '@/forensicEvents';

const BASE_URL = 'https://bronze-tracker-resources.s3.eu-central-1.amazonaws.com/videos/';

/**
 * The video of each app language, with its length (for the Ajustes hint, before the file is touched): the
 * file's duration rounded up, as the player's countdown does, so the hint and the first «Quedan» agree.
 */
export const PHOTO_VIDEOS = Object.freeze({
  es: Object.freeze({ file: 'buenas-fotos-es-v1.mp4', durationS: 105 }),
  en: Object.freeze({ file: 'buenas-fotos-en-v1.mp4', durationS: 112 }),
  fr: Object.freeze({ file: 'buenas-fotos-fr-v1.mp4', durationS: 112 }),
  pt: Object.freeze({ file: 'buenas-fotos-pt-v1.mp4', durationS: 105 }),
});

/** The video for the running locale ('es', 'pt-PT'…), or null when that language has none yet. */
export function photoVideoFor(locale) {
  const lang = typeof locale === 'string' ? locale.split('-')[0].toLowerCase() : '';
  const entry = Object.prototype.hasOwnProperty.call(PHOTO_VIDEOS, lang) ? PHOTO_VIDEOS[lang] : null;
  if (!entry) return null;
  return { locale: lang, file: entry.file, url: BASE_URL + entry.file, durationS: entry.durationS };
}

/** Seconds as a clock, «1:44»: whole seconds, rounded up so the count reaches 0:00 only at the end. */
export function formatClock(seconds) {
  const s = Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : 0;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * A jump in the playhead larger than this between two `timeupdate`s (about four a second) is not watching:
 * a replay from the start or a buffer catching up. It does not count as seconds watched.
 */
export const MAX_WATCH_STEP_S = 1.5;

/**
 * `event`: shown · played · completed · closed (the table in the doc). Becomes `fx_photo_video_<event>`.
 */
export function trackPhotoVideo(event, props = {}) {
  try {
    // typeof: the constant only exists in the webpack build (see vue.config.js), not in vitest.
    if (typeof __BRONZE_DEMO__ !== 'undefined' && __BRONZE_DEMO__) return;
    reportForensicEvent(`photo_video_${event}`, props);
  } catch (_) { /* never let telemetry throw */ }
}
